import { ActionIcon, Box, Tooltip, type ActionIconProps } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState } from "react";

import AppSettings from "src/AppSettings";
import Icon from "src/components/common/Icon";
import HelpTourTarget from "src/components/help/HelpTourTarget";
import { ApplicationTheme } from "src/contexts/preferences/UserPreferencesContext";
import { helpTourTargets } from "src/help/helpTours";
import useHelp from "src/hooks/help/useHelp";
import useUserPreferences from "src/hooks/preferences/useUserPreferences";
import openSearch from "src/routing/searchLauncher";

import PreferencesModal from "./PreferencesModal";

/**
 * Defines the inputs supplied to this component.
 */
interface IHeaderActionProps {
    size?: ActionIconProps["size"];
}

/**
 * Renders the action that dispatches the shared search-open event.
 */
export const SearchAction = ({ size }: IHeaderActionProps = {}) => (
    <Tooltip
        label="Search"
        withArrow
    >
        <ActionIcon
            variant="subtle"
            color="gray"
            onClick={openSearch}
            aria-label="Search"
            size={size}
            bdrs={0}
        >
            <Icon icon={AppSettings.icons.search} />
        </ActionIcon>
    </Tooltip>
);

/**
 * Renders the toggle for the current page's help panel.
 *
 * Postconditions: Renders nothing without a help provider, so only the modern layout offers help.
 * The wrapping box is the header's tour target; the button itself carries the tooltip.
 */
export const HelpAction = ({ size }: IHeaderActionProps = {}) => {
    const { available, opened, toggle } = useHelp();

    if (!available) {
        return null;
    }

    return (
        <HelpTourTarget id={helpTourTargets.help}>
            <Box>
                <Tooltip
                    label={opened ? "Close help" : "Help"}
                    withArrow
                >
                    <ActionIcon
                        variant={opened ? "light" : "subtle"}
                        color="gray"
                        onClick={toggle}
                        aria-label={opened ? "Close help" : "Help"}
                        aria-pressed={opened}
                        size={size}
                        bdrs={0}
                    >
                        <Icon icon={AppSettings.icons.help} />
                    </ActionIcon>
                </Tooltip>
            </Box>
        </HelpTourTarget>
    );
};

/**
 * Renders a theme toggle based on the stored preference.
 *
 * Postconditions: Clicking requests the opposite stored theme; effective legacy presentation
 * still follows the preference provider's light-theme rule.
 */
export const ThemeToggleAction = ({ size }: IHeaderActionProps = {}) => {
    const { preferences, toggleTheme } = useUserPreferences();
    const dark = preferences.theme === ApplicationTheme.Dark;

    return (
        <Tooltip
            label={dark ? "Use light theme" : "Use dark theme"}
            withArrow
        >
            <ActionIcon
                variant="subtle"
                color="gray"
                onClick={toggleTheme}
                aria-label={dark ? "Use light theme" : "Use dark theme"}
                size={size}
                bdrs={0}
            >
                <Icon icon={dark ? AppSettings.icons.themeLight : AppSettings.icons.themeDark} />
            </ActionIcon>
        </Tooltip>
    );
};

/**
 * Owns the disclosure state for the preferences button and its modal.
 */
export const PreferencesAction = ({ size }: IHeaderActionProps = {}) => {
    const [opened, handlers] = useDisclosure(false);

    return (
        <>
            <Tooltip
                label="Preferences"
                withArrow
            >
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={handlers.open}
                    aria-label="Preferences"
                    size={size}
                    bdrs={0}
                >
                    <Icon icon={AppSettings.icons.preferences} />
                </ActionIcon>
            </Tooltip>
            <PreferencesModal
                opened={opened}
                onClose={handlers.close}
            />
        </>
    );
};

/**
 * Attaches the initially available Sentry feedback integration to the enabled report button.
 *
 * Postconditions: The effect detaches its listener on cleanup. Missing feedback or a missing
 * button leaves no attachment; the integration is captured once for this component instance.
 */
export const ReportBugAction = ({ size }: IHeaderActionProps = {}) => {
    const [feedback] = useState(() => Sentry.getFeedback());
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(
        () => {
            if (!buttonRef.current) {
                return;
            }

            const unsubscribe = feedback?.attachTo(buttonRef.current, {});
            return () => unsubscribe?.();
        },
        [feedback]
    );

    if (!AppSettings.sentry.enableReportABug) {
        return null;
    }

    return (
        <Tooltip
            label="Report a Bug"
            withArrow
        >
            <ActionIcon
                variant="subtle"
                color="gray"
                ref={buttonRef}
                aria-label="Report a Bug"
                size={size}
                bdrs={0}
            >
                <Icon icon={AppSettings.icons.reportABug} />
            </ActionIcon>
        </Tooltip>
    );
};
